import { inject, Injectable } from '@angular/core';
import { PlatformService } from '@core/platform';
import { WINDOW } from '@innogy/utils-dom';
import type { DatalayerOutput, GDDLEvent } from '@analytics/models';
import { ConfigLoaderService } from '@core/config-angular';

@Injectable({
  providedIn: 'root',
})
export class GddlInspectorService implements DatalayerOutput {
  readonly #windowRef = inject(WINDOW);
  readonly #platform = inject(PlatformService);
  readonly #config = inject(ConfigLoaderService);
  #inspectorEnabled = false;

  // Key used to store the event log in sessionStorage.
  readonly #GDDLInspectorEventLogKey = 'eventLog';

  readonly id = 'GDDL_INSPECTOR';

  async init() {
    this.#inspectorEnabled = (
      await this.#config.getConfigAsync()
    ).debug.analyticsInspector;

    if (this.#inspectorEnabled) {
      console.info(
        '📊 %cAnalytics Debugging enabled.',
        'color: blue; font-weight: bold;'
      );
    }
  }

  #logEvent(event: GDDLEvent) {
    const date = new Date();
    console.groupCollapsed(
      `%c${date.toLocaleTimeString()}.${date.getMilliseconds()}%c Analytics event
          %c${event.event}: ${event.info['name'] ?? event.info['variant'] ?? (event as any).pageInfo.path}`,
      'background-color: rgba(0,0,220,0.1); border-radius: 4px; padding: 2px; font-size: 8px;',
      'color: inherit; font-weight: bold;',
      'color: inherit; font-weight: normal;'
    );
    console.log(event);
    console.groupEnd();
  }

  /**
   * Dispatches a GDDL event to the inspector.
   *
   * @param event - The GDDLEvent object to be logged and dispatched.
   * Appends the event to the sessionStorage log and sends a message to notify listeners.
   */
  public dispatchEvent(event: GDDLEvent) {
    const storage = this.#windowRef.sessionStorage;
    const eventLog = storage.getItem(this.#GDDLInspectorEventLogKey);

    // Appends the serialized event to the existing event log.
    const logWithAppendedEvent = `${eventLog},${JSON.stringify(event)}`;
    storage.setItem(this.#GDDLInspectorEventLogKey, logWithAppendedEvent);

    // Notifies listeners about the analytics event change.
    this.#windowRef.postMessage('analytics-track-change', '*');

    if (this.#inspectorEnabled) {
      this.#logEvent(event);
    }
  }

  /**
   * Determines whether the output channel can receive events
   * @returns boolean indicating whether or not the output channel should have events forwarded to it.
   */
  public outputConditionsAreMet(): boolean {
    return (
      this.#platform.isClient() &&
      typeof this.#windowRef.sessionStorage?.setItem === 'function'
    );
  }
}
